import React, { useEffect, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import MuiWrapper from 'components/common/input/MuiWrapper';
import { IconButton, TextField } from '@material-ui/core';
import { T } from 'components/common/text';
import { icon_notice, icon_plus_secondary } from 'components/base/images';
import SearchIcon from '@material-ui/icons/Search';
import CustomSpan from 'components/common/text/CustomSpan';
import { IPatient, TRememberPatient } from 'lib/types/patientTypes';
import { Clear } from '@material-ui/icons';
import CircularLoading from 'components/base/loading/CircularLoading';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useShallowAppSelector } from 'store/hooks';
import { calculateAge } from 'lib/library';
import { DateConverter } from 'components/common/convert';
import cx from 'classnames';
import { useQuickstartStore } from 'store/local/quickstart';
import { useQuickStart } from 'lib/hooks/quickstart/useQuickStart';
import { useLocation } from 'react-router-dom';
import storage, { keys } from 'lib/storage';
import { useDidUpdateEffect } from 'lib/utils';
import moment from 'moment';
import { oemState } from 'lib/store';

function QuickSearch() {
  const { t } = useTranslation();
  let searchPatientTimer: any = null; // 환자 검색 시 사용할 타이머 변수
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  const {
    rememberPatient,
    setSearchInputRef,
    searchInputRef,
    searchResultRef,
    searchKeyword,
    setSearchKeyword,
    prevSearchKeyword,
    setPrevSearchKeyword,
    selectedPatientCode,
    setSelectedPatientCode,
    isOpenSearchResult,
    patientListLoadSuccess,
    setPatientListLoadSuccess,
    patientList,
    setPatientList,
    searchData,
    isChangeKeyword,
    isSearchFocus,
    setIsOpenSearchResult,
    setRememberPatient,
    setIsChangeKeyword,
  } = useQuickstartStore();
  const {
    language,
    patientNameConvert,
    hasMorePatients,
    onFetchPatientListMore,
    handleChangeSearchKeyword,
    handleFocusSearchBar,
    handleBlurSearchBar,
    handleCancelKeywordChange,
    handleClearSearch,
    handleOpenPatientModal,
    handleClickSearchResult,
    handleSearchInit,
    handleSearchPatient,
    fetchPatientList,
    fetchPatientListSuccess,
    handleClickRememberPatient,
    handleClearState,
    searchInputReference,
    handleClickRemember,
  } = useQuickStart();

  return (
    <StyledQuickSearch
      data-component-name="QuickSearch"
      className={cx({
        selected: !!selectedPatientCode,
      })}
    >
      <div
        className={cx('quickSearch__title_wrapper', {
          search: !!selectedPatientCode,
        })}
      >
        {!!selectedPatientCode ? (
          <CustomSpan
            fontColor={oemState() === 'marathon' ? '#fff' : '#000'}
            fontSize={26}
            fontWeight={600}
          >
            <T>GLOBAL_PATIENT_INFO</T>
          </CustomSpan>
        ) : (
          <>
            <CustomSpan
              fontColor={oemState() === 'marathon' ? '#fff' : '#222'}
              fontSize={40}
              fontWeight={500}
            >
              <T>QUICKSTART_SUB_TITLE</T>
            </CustomSpan>
            <CustomSpan fontColor="#444" fontSize={20} fontWeight={500}>
              <T>QUICKSTART_SUB_DETAIL</T>
            </CustomSpan>
          </>
        )}
      </div>
      <div
        className={cx('quickSearch__search_wrapper', {
          search: !!selectedPatientCode,
        })}
        ref={searchResultRef}
      >
        <MuiWrapper
          className="quickSearch__search_bar_box"
          config={{
            borderColor: 'transparent',
            activeBorderColor: 'transparent',
            hoverBorderColor: 'transparent',
          }}
        >
          <TextField
            minRows={1}
            maxRows={1}
            variant="outlined"
            fullWidth
            placeholder={t('SEACH_FOR_PATIENT')}
            value={searchKeyword || ''}
            onChange={handleChangeSearchKeyword}
            onFocus={handleFocusSearchBar}
            onBlur={() => {
              setTimeout(() => {
                handleBlurSearchBar();
              }, 200);
            }}
            onKeyDown={(e: any) => {
              if (e.key === 'Escape') {
                handleCancelKeywordChange();
              }
            }}
            inputRef={searchInputReference}
            InputProps={{
              startAdornment: (
                <div className="icon_search_box">
                  <SearchIcon fontSize="inherit" color="inherit" />
                </div>
              ),
              endAdornment: searchKeyword.length > 0 && !!isSearchFocus && (
                <IconButton
                  className="icon_clear_box"
                  onClick={() => {
                    handleClearSearch();
                  }}
                  edge="end"
                >
                  <div className="icon_clear">
                    <Clear fontSize="inherit" color="inherit" />
                  </div>
                </IconButton>
              ),
            }}
          />
        </MuiWrapper>
        <IconButton
          className="quickSearch__new_patien_box"
          onClick={() => {
            handleOpenPatientModal('new');
          }}
        >
          <div className="quickSearch__new_patien">
            <div className="new_patien_add">
              <img src={icon_plus_secondary} />
            </div>
            <CustomSpan fontColor="#222" fontSize={24} fontWeight={400}>
              <T>GLOBAL_NEW_PATIENT</T>
            </CustomSpan>
          </div>
        </IconButton>
        <div
          className={cx('quickSearch__search_box', {
            open: !!isOpenSearchResult,
          })}
        >
          {!!isOpenSearchResult && (
            <>
              {!!patientListLoadSuccess ? (
                <div className="quickSearch__search_list_box">
                  {patientList.length > 0 ? (
                    <>
                      <div className="quickSearch__search_list_row header">
                        <div className="search_list_row_item name">
                          <CustomSpan fontColor="#888" fontSize={20} fontWeight={400}>
                            <T>PATIENT_INFO_FIRST_NAME</T>
                          </CustomSpan>
                        </div>
                        <div className="search_list_row_item chart">
                          <CustomSpan fontColor="#888" fontSize={20} fontWeight={400}>
                            <T>PATIENT_INFO_CHART_NUM</T>
                          </CustomSpan>
                        </div>
                        <div className="search_list_row_item enroll">
                          <CustomSpan fontColor="#888" fontSize={20} fontWeight={400}>
                            <T>PATIENT_INFO_BIRTH_DAY</T>
                          </CustomSpan>
                        </div>
                        <div className="search_list_row_item age">
                          <CustomSpan fontColor="#888" fontSize={20} fontWeight={400}>
                            <T>GLOBAL_AGE</T>
                          </CustomSpan>
                        </div>
                        <div className="search_list_row_item pms">
                          <CustomSpan fontColor="#888" fontSize={20} fontWeight={400}>
                            <T>PATIENT_CONNECT</T>
                          </CustomSpan>
                        </div>
                      </div>
                      <InfiniteScroll
                        className="quickSearch__search_list"
                        dataLength={patientList?.length}
                        next={onFetchPatientListMore}
                        hasMore={hasMorePatients}
                        loader={
                          <div className="infinite-scroll-loading">
                            <CircularLoading />
                          </div>
                        }
                        height={200}
                        scrollThreshold={0.95}
                      >
                        {patientList.map((item: IPatient, idx: number) => {
                          const patientName = patientNameConvert(item);
                          const patientAge = calculateAge(item.birthday);
                          return (
                            <div
                              className="quickSearch__search_list_row"
                              key={idx}
                              onClick={() => {
                                handleClickSearchResult(item);
                              }}
                            >
                              <div className="search_list_row_item name">
                                <CustomSpan fontColor="#222" fontSize={24} fontWeight={400}>
                                  {patientName}{' '}
                                  {!!item.identification ? `(${item.identification})` : ''}
                                </CustomSpan>
                              </div>

                              <div className="search_list_row_item chart">
                                <CustomSpan fontColor="#222" fontSize={24} fontWeight={400}>
                                  {item.chartNumber}
                                </CustomSpan>
                              </div>
                              <div className="search_list_row_item enroll">
                                <CustomSpan fontColor="#222" fontSize={24} fontWeight={400}>
                                  {moment(item?.birthday).isValid() ? (
                                    <DateConverter
                                      language={language}
                                      timestamp={moment(item?.birthday).unix()}
                                      format={language === 'en' ? 'MMM/DD/YYYY' : 'YYYY/MM/DD'}
                                    />
                                  ) : (
                                    `-`
                                  )}
                                </CustomSpan>
                              </div>
                              <div className="search_list_row_item age">
                                <CustomSpan fontColor="#222" fontSize={24} fontWeight={400}>
                                  {!!patientAge.age || patientAge.age === 0 ? patientAge.age : '-'}
                                </CustomSpan>
                              </div>
                              <div className="search_list_row_item pms">
                                <CustomSpan fontColor="#222" fontSize={24} fontWeight={400}>
                                  {!!item.pms ? 'C' : '-'}
                                </CustomSpan>
                              </div>
                            </div>
                          );
                        })}
                      </InfiniteScroll>
                    </>
                  ) : (
                    <div className="quickSearch__search_empty">
                      <CustomSpan fontColor="#888" fontSize={24} fontWeight={400}>
                        {t(`QUICKSTART_SEARCH_EMPTY`, {
                          searchData: searchData,
                        })}
                      </CustomSpan>
                    </div>
                  )}
                  {!!patientListLoadSuccess && (
                    <div className="quickSearch__search_comment_row">
                      <div className="quickSearch__search_comment_box">
                        <div className="quickSearch__search_comment_icon">
                          <img src={icon_notice} />
                        </div>
                        <div className="quickSearch__search_comment">
                          <CustomSpan fontColor="#25256F" fontSize={18} fontWeight={400}>
                            {patientList.length > 0 ? (
                              <T>QUICKSTART_EXIST_PATIENT</T>
                            ) : (
                              <T>QUICKSTART_EMPTY_PATIEN</T>
                            )}
                          </CustomSpan>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="infinite-scroll-loading">
                  <CircularLoading />
                </div>
              )}
            </>
          )}
        </div>
      </div>
      {!selectedPatientCode && (
        <div
          className={cx('quickSearch__remember_wrapper', {
            hidden: !!selectedPatientCode,
          })}
        >
          <CustomSpan fontColor="#25256F" fontSize={18} fontWeight={600}>
            <T>QUICKSTART_SEARCH_REMEMBER</T>
          </CustomSpan>
          <div className="quickSearch__remember_list">
            {rememberPatient.length > 0 &&
              rememberPatient?.map((patient: TRememberPatient, idx: number) => {
                console.log('HDS] rememberPatient _________ ', patient);
                if (!patient.code || !patient.name) return;
                return (
                  <IconButton
                    className="quickSearch__remember_item"
                    key={idx}
                    onClick={() => {
                      handleClickRemember(patient);
                    }}
                  >
                    <div className="quickSearch__remember">
                      <div className="quickSearch__remember_icon">
                        <SearchIcon fontSize="inherit" color="inherit" />
                      </div>
                      <CustomSpan fontColor="#444" fontSize={20} fontWeight={600}>
                        {patient.name}
                      </CustomSpan>
                    </div>
                  </IconButton>
                );
              })}
          </div>
        </div>
      )}
    </StyledQuickSearch>
  );
}

const StyledQuickSearch = styled.div<{}>`
  height: 850px;
  padding-right: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  &.selected {
    display: initial;
    height: initial;
  }
  .quickSearch__title_wrapper {
    display: flex;
    flex-direction: column;
    row-gap: 20px;
    align-items: center;
    &.search {
      align-items: flex-start;
    }
  }
  .quickSearch__search_wrapper {
    display: flex;
    column-gap: 10px;
    position: relative;
    margin: 60px 0 40px 0;
    &.search {
      margin: 15px 0 20px 0;
    }
    .quickSearch__search_bar_box {
      width: 1010px;
      height: 80px;
      border-radius: 10px;
      border: 1px solid #e1e1e7;
      overflow: hidden;
      .MuiOutlinedInput-input {
        font-size: 24px;
        font-weight: 400;
      }
      .icon_search_box {
        width: 34px;
        height: 34px;
        color: #5b5b5b;
        font-size: 34px;
        margin-right: 16px;
      }

      .icon_clear_box {
        width: 34px;
        height: 34px;
        padding: 10px;
        margin-right: 5px;
        &.hide {
          visibility: hidden;
        }
        .icon_clear {
          font-size: 24px;
        }
      }
    }
    .quickSearch__new_patien_box {
      width: 180px;
      background-color: #fff;
      border-radius: 10px;
      border: 1px solid #e1e1e7;

      .quickSearch__new_patien {
        display: flex;
        align-items: center;
        column-gap: 10px;
        .new_patien_add {
          width: 24px;
          height: 24px;
        }
      }
    }

    .quickSearch__search_box {
      position: absolute;
      top: 85px;
      left: 0;
      width: 1010px;
      min-height: 110px;
      z-index: 100;
      padding: 10px;
      background-color: #fff;
      border: 1px solid #e1e1e7;
      border-radius: 10px;
      display: none;
      &.open {
        display: block;
      }

      .quickSearch__search_list_box {
        width: 990px;
        .quickSearch__search_list {
          height: 200px;
          overflow-y: overlay;
        }
        .quickSearch__search_list_row {
          display: flex;
          width: 100%;
          height: 50px;
          &:not(.header) {
            &:hover {
              background-color: #f9f9fb;
              cursor: pointer;
              border-radius: 5px;
            }
          }
          .search_list_row_item {
            display: flex;
            align-items: center;
            justify-content: flex-start;
            padding: 10px;
            &.name {
              width: 230px;
            }
            &.chart {
              width: 250px;
            }
            &.enroll {
              width: 220px;
            }
            &.age {
              width: 210px;
            }
            &.pms {
            }
          }
        }
        .quickSearch__search_empty {
          margin-top: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 10px;
        }
        .quickSearch__search_comment_row {
          margin: 20px 0;
          width: 100%;
          display: flex;
          justify-content: center;
          .quickSearch__search_comment_box {
            display: flex;
            column-gap: 5px;
            align-items: center;
            justify-content: center;
            border-radius: 30px;
            background-color: #f9f9fb;
            padding: 10px 14px;
          }
        }
      }
    }
  }
  .quickSearch__remember_wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 20px;
    &.hidden {
      display: none;
    }
    .quickSearch__remember_list {
      display: flex;
      column-gap: 10px;
      .quickSearch__remember_item {
        padding: 12px 16px;
        border: 1px solid #e1e1e7;
        border-radius: 50px;
        background-color: #fff;
        .quickSearch__remember {
          display: flex;
          align-items: center;
          justify-content: center;
          column-gap: 10px;
          .quickSearch__remember_icon {
            color: #ccccd4;
            width: 20px;
            height: 20px;
          }
        }
      }
    }
  }
`;

export default React.memo(QuickSearch);
